import classNames from "classnames";
import { parse as optimisticParse } from "best-effort-json-parser";
import React, { ReactElement, useState, useCallback } from "react";
import { AiSummaryProps, AiSummaryState } from "./AiSummary.types";
import { localize, localizeFormat } from "src/l10n";
import Visage2Icon from "src/visage2/Visage2Icon/Visage2Icon";
import { MediumBodyBolder, SmallBody } from "../Text";
import { SearchLoader } from "../MainHeaderSearch/SearchLoader";
import { Conditional } from "../Conditional";
import { fetchEventSource } from "@microsoft/fetch-event-source";
import { useSelector } from "react-redux";

/**
 * AiSummary component
 *
 * @param {AiSummaryProps} props - The component props
 * @returns {ReactElement | null} The rendered component
 */
function AiSummary({
    title,
    content,
}: AiSummaryProps): ReactElement | null {
    const [state, setState] = useState<AiSummaryState>({
        generating: false,
    });

    const enabled = useSelector<Spintr.AppState, boolean>(
        (appState) => appState.instance.get("enableAiSummary") as boolean);

    const { generating, requested, result } = state;

    const onClicked = useCallback(() => {
        if (requested) {
            return;
        }

        setState((prevState) => ({
            ...prevState,
            requested: true,
            generating: true,
        }));

        let json = "";
        const abortController = new AbortController();
        fetchEventSource("/api/v1/insights/article-summary", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify({
                title, content
            }),
            signal: abortController.signal,

            onerror: (err) => {
                console.log(err);
                abortController.abort();
                setState((prevState) => ({
                    ...prevState,
                    generating: false,
                    result: undefined,
                }));
                throw err;
            },

            onmessage: (event) => {
                const text = event.data;
                json += text;

                setState((prevState) => ({
                    ...prevState,
                    result: optimisticParse(json),
                }));
            },

            onopen: async (response) => {
                if (response.ok) {
                    return;
                }

                throw new Error(`Failed to open connection: ${response.status} ${response.statusText}`);
            },

            onclose: () => {
                setState((prevState) => ({
                    ...prevState,
                    generating: false,
                    result: optimisticParse(json),
                }));
            },
        });
    }, [content, requested, setState, title]);

    if (!enabled || (requested && !generating && !result?.summary)) {
        return null;
    }

    if (!content || content.length < 200) {
        return null;
    }

    const headerText = !requested
        ? localize("SHOW_AI_SUMMARY")
        : (generating
            ? localize("FETCHING_AI_SUMMARY")
            : localizeFormat("AI_SUMMARY_OF", title));

    return (
        <div className={classNames("AiSummary", {
            "expanded": result?.summary?.length > 0,
        })}>
            <div className="AiSummary-header" onClick={onClicked}>
                <Visage2Icon color="white"
                             icon="bard-fill"
                             size="big"
                             type="custom" />
                <MediumBodyBolder color="white">
                    {headerText}
                </MediumBodyBolder> 
                <Conditional condition={generating}>
                    <SearchLoader size={24}/>
                </Conditional>
            </div>
            <div className="AiSummary-content">
                <SmallBody className="summary-body"
                           color="contentDark">
                    {result?.summary ?? ""}
                </SmallBody>
            </div>
        </div>
    );
}

export default AiSummary;

